import { useEffect, useState, type FormEvent } from 'react'
import { Globe, KeyRound, Plus, Search, Ticket, UserRoundX } from 'lucide-react'
import { api } from '../../api/client'
import type { User } from '../../api/types'
import { EmptyState } from '../../components/EmptyState'
import { Modal } from '../../components/Modal'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

function regSourceLabel(user: User): { label: string; color: string; icon: typeof Globe } {
  if (user.regSource === 'sso' || user.oidcIssuer) {
    return { label: user.oidcIssuer ? new URL(user.oidcIssuer).hostname : 'SSO', color: 'bg-blue-100 text-blue-700', icon: Globe }
  }
  if (user.regSource === 'invitation') {
    return { label: '邀请码', color: 'bg-violet-100 text-violet-700', icon: Ticket }
  }
  return { label: '本站', color: 'bg-emerald-100 text-emerald-700', icon: Globe }
}

export function UsersPage() {
  const { t } = useI18n()
  const [items, setItems] = useState<User[]>([])
  const [query, setQuery] = useState('')
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [resetModal, setResetModal] = useState<User | null>(null)
  const [newPassword, setNewPassword] = useState('')

  const load = () => api.get<User[]>('/admin/users').then(setItems).catch((reason: Error) => setError(reason.message))
  useEffect(() => { void load() }, [])

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    try {
      const item = await api.post<User>('/admin/users', { email: form.get('email'), username: form.get('username'), password: form.get('password'), quotaMb: Number(form.get('quotaMb')) })
      setItems((current) => [item, ...current])
      setShowForm(false)
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to create user.') }
  }

  async function toggleDisabled(user: User) {
    try {
      await api.patch(`/admin/users/${user.id}`, { disabled: !user.disabled, quotaMb: user.quotaMb })
      setItems((current) => current.map((item) => item.id === user.id ? { ...item, disabled: !item.disabled } : item))
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to update user.') }
  }

  async function resetPassword() {
    if (!resetModal || newPassword.length < 8) return
    try {
      await api.post(`/admin/users/${resetModal.id}/reset-password`, { password: newPassword })
      setResetModal(null)
      setNewPassword('')
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to reset password.') }
  }

  const visible = items.filter((item) => `${item.email} ${item.username}`.toLowerCase().includes(query.toLowerCase()))

  return <>
    <PageHeader title="admin.users" action={
      <button onClick={() => setShowForm((v) => !v)} className="btn-primary flex items-center gap-2"><Plus size={16} />{t('admin.addUser')}</button>
    } />

    {showForm && (
      <form onSubmit={create} className="panel mb-5 p-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <input className="field" name="email" type="email" placeholder={t('common.email')} required />
          <input className="field" name="username" placeholder={t('auth.username')} required />
          <input className="field" name="password" type="password" placeholder={t('common.password')} required minLength={8} />
          <input className="field" name="quotaMb" type="number" min="1" defaultValue="1024" placeholder={t('admin.quota') + ' (MB)'} />
        </div>
        <div className="mt-4 flex gap-3">
          <button className="btn-primary">{t('admin.addUser')}</button>
          <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">{t('common.cancel')}</button>
        </div>
      </form>
    )}

    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
      <label className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-3 text-muted-foreground" size={16} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} className="field pl-9" aria-label={t('common.search')} placeholder={t('common.search')} />
      </label>
      <span className="text-sm text-muted-foreground">{visible.length} {t('admin.mailboxUsers')}</span>
    </div>

    {error && <p className="mb-4 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{error}</p>}

    {visible.length === 0 ? <EmptyState title="admin.emptyUsers" /> : (
      <>
        {/* Mobile card view */}
        <div className="space-y-3 sm:hidden">
          {visible.map((item) => {
            const source = regSourceLabel(item)
            return (
              <div key={item.id} className="panel p-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{item.email}</p>
                    <p className="text-xs text-muted-foreground">@{item.username}</p>
                  </div>
                  <span className={`badge ${item.disabled ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {item.disabled ? '已禁用' : '正常'}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className={`badge ${item.role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}>{item.role}</span>
                  <span className={`badge ${source.color}`}><source.icon size={12} />{source.label}</span>
                  <span className="text-xs text-muted-foreground">{item.quotaMb} MB</span>
                </div>
                <div className="mt-3 flex gap-2 border-t pt-3">
                  <button onClick={() => toggleDisabled(item)} className="btn-secondary h-8 px-3 text-xs">{item.disabled ? '启用' : '禁用'}</button>
                  <button onClick={() => setResetModal(item)} className="btn-secondary h-8 px-3 text-xs">重置密码</button>
                </div>
              </div>
            )
          })}
        </div>

        {/* Desktop table view */}
        <div className="hidden sm:block panel overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('common.email')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.role')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.quota')}</th>
                  <th className="px-4 py-3 font-medium">渠道</th>
                  <th className="px-4 py-3 font-medium">{t('common.status')}</th>
                  <th className="px-4 py-3 text-right font-medium">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {visible.map((item) => {
                  const source = regSourceLabel(item)
                  return (
                    <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium">{item.email}</p>
                        <p className="text-xs text-muted-foreground">@{item.username}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${item.role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}>{item.role}</span>
                      </td>
                      <td className="px-4 py-3">{item.quotaMb} MB</td>
                      <td className="px-4 py-3">
                        <span className={`badge ${source.color}`}><source.icon size={12} />{source.label}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${item.disabled ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {item.disabled ? '已禁用' : '正常'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <button onClick={() => toggleDisabled(item)} className="rounded-lg border p-2 transition-colors hover:bg-muted" title={item.disabled ? '启用' : '禁用'}>
                            <UserRoundX size={15} />
                          </button>
                          <button onClick={() => setResetModal(item)} className="rounded-lg border p-2 transition-colors hover:bg-muted" title="重置密码">
                            <KeyRound size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </>
    )}

    <Modal open={!!resetModal} onClose={() => { setResetModal(null); setNewPassword('') }} title={`重置密码 - ${resetModal?.email ?? ''}`}>
      <div className="space-y-4">
        <input className="field" type="password" placeholder="新密码（至少8位）" minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        <div className="flex gap-3">
          <button onClick={resetPassword} disabled={newPassword.length < 8} className="btn-primary disabled:opacity-50">确认重置</button>
          <button onClick={() => { setResetModal(null); setNewPassword('') }} className="btn-secondary">{t('common.cancel')}</button>
        </div>
      </div>
    </Modal>
  </>
}
